import { createFileRoute } from "@tanstack/react-router";
import { PEOPLE_ORIGIN, marketingHead } from "@/lib/marketing/seo";
import { PUBLISHER, SITE_NAME } from "@/lib/marketing/ppl-schema";
import { FILMS_ON, FILM_OG_VIDEO, FILM_VIDEO_OBJECT } from "@/lib/marketing/film";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { Swap, useRotatingWord } from "@/components/marketing/site/rotating-word";
import { useSceneFallback } from "@/components/marketing/site/people-scene";
import { CosmosSky } from "@/components/marketing/site/cosmos/cosmos-sky";
import { CosmosOpening } from "@/components/marketing/site/cosmos/cosmos-opening";
import { WarpDeck, type DeckChapter } from "@/components/marketing/site/deck/warp-deck";
import { PanelHours } from "@/components/marketing/site/why/panel-hours";
import { PanelFilm } from "@/components/marketing/site/why/panel-film";
import { PanelLeaks } from "@/components/marketing/site/why/panel-leaks";
import { PanelDesks } from "@/components/marketing/site/why/panel-desks";
import { PanelRecord } from "@/components/marketing/site/why/panel-record";
import { OPENING } from "@/components/marketing/site/why/why-content";
import { LazyMotion } from "motion/react";
import { EMPLOYEES } from "@alm/domain/marketing";
/* The domain module, not lib/marketing/journey-v1: that wrapper hangs an icon
   on each stage, and an import of it from a route's head() puts the whole
   icon set on the critical path of every page. */
import { JOURNEY_FEATURE_LIST } from "@alm/domain/marketing/journey";
import "@/styles/people-cosmos.css";
import "@/styles/people-cosmos-sections.css";
import "@/styles/people-cosmos-deck.css";
import "@/styles/people-cosmos-panels.css";
import "@/styles/people-cosmos-panel-scenes.css";
import "@/styles/people-cosmos-film.css";

/* The home page: the opening, then four outcome panels in one sky; the deck turns in 3D while
 * the sky jumps (deck/warp-deck.tsx). No price appears here, by decision: /demo starts that
 * conversation. */

/* schema */

/* WebSite and Organization nodes are on every arm page (lib/marketing/ppl-schema.ts). No offers
 * node: the surface publishes no figure. The FAQPage node lives on /operating-system. */
const WORKFORCE_SOFTWARE = {
  "@type": "SoftwareApplication",
  name: SITE_NAME,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: `${PEOPLE_ORIGIN}/`,
  publisher: PUBLISHER,
  description:
    "An operating system built for AI to operate, with humans assisting: AI employees run admissions, front desk, billing, staffing, facilities, care documentation and compliance on one senior-living system of record.",
  /* The roster alone described a workforce with nothing underneath it. The journey stages lead
   * because a crawler asking what this software does wants the operation named before the staff are
   * — and the two lists together are the claim the page itself makes. */
  featureList: [
    ...JOURNEY_FEATURE_LIST,
    ...EMPLOYEES.map((employee) => `${employee.name}: ${employee.role}`),
  ],
};

const loadMotionFeatures = () =>
  import("@/components/marketing/site/cosmos/motion-features").then((m) => m.default);

/** The rotation starts once the tagline has finished its own reveal. */
const ROTATOR_START_MS = 3400;

function MarketingLanding() {
  const sky = useSceneFallback();
  const rotator = useRotatingWord(ROTATOR_START_MS);

  /* each panel's tint: indigo, blue, violet, cyan, magenta */
  const chapters: DeckChapter[] = [
    { id: "workforce", shot: "workforce", hue: 232, node: <PanelHours rotator={rotator} /> },
    { id: "film", shot: "workforce", hue: 214, node: <PanelFilm /> },
    { id: "two", shot: "two", hue: 262, node: <PanelLeaks /> },
    { id: "roster", shot: "roster", hue: 196, node: <PanelDesks /> },
    { id: "operating-system", shot: "os", hue: 286, node: <PanelRecord /> },
  ];

  return (
    <MarketingShell sky={false}>
      <LazyMotion features={loadMotionFeatures} strict>
        <div className="cosmos" data-sky={sky.unavailable ? "still" : undefined}>
          <CosmosSky onUnavailable={sky.markUnavailable} gates />
          <CosmosOpening
            rotator={rotator}
            copy={{
              label: OPENING.label,
              title: (
                <>
                  {OPENING.title} <Swap {...rotator} />
                </>
              ),
              sub: OPENING.sub,
            }}
          />
          <div className="people-light">
            <WarpDeck chapters={chapters} />
          </div>
          <div className="cosmos-end" data-shot="end" data-warp-shot="end" aria-hidden="true" />
        </div>
      </LazyMotion>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/")({
  component: MarketingLanding,
  head: () =>
    marketingHead({
      /* Each under what a result page shows (600px of title, 155 characters of
         description), so neither is cut mid-word. */
      title: "PEOPLE AI Workforce (PPL AI): AI employees for assisted living",
      description:
        "PEOPLE AI Workforce (PPL AI) puts AI employees to work on one senior-living operating system, from admissions to billing. Hire one, or the whole company.",
      path: "/v2/",
      imageAlt: "PEOPLE AI Workforce. The AI workforce for assisted living communities.",
      video: FILMS_ON ? FILM_OG_VIDEO : undefined,
      schema: FILMS_ON ? [WORKFORCE_SOFTWARE, FILM_VIDEO_OBJECT] : [WORKFORCE_SOFTWARE],
    }),
});
